import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

type ProductItem = {
  title: string;
  image: string;
  description?: string;
};

type ProductCategoryPageProps = {
  title: string;
  subtitle: string;
  heroImage: string;
  products: ProductItem[];
  onBack: () => void;
};

export const ProductCategoryPage: React.FC<ProductCategoryPageProps> = ({
  title,
  subtitle,
  heroImage,
  products,
  onBack,
}) => {
  const { isDark } = useTheme();

  return (
    <>
      {/* Hero Section */}
      <section className="relative w-full py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={heroImage} 
            alt={title} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-vmil-blue/10" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Products</span>
          </button>
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6">
              {title.split(' ').slice(0, -1).join(' ')}
              <span className="block text-vmil-blue">{title.split(' ').slice(-1)[0]}</span>
            </h1>
            <p className="text-xl text-white/80">
              {subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className={`w-full py-16 md:py-24 ${isDark ? 'bg-black' : 'bg-white'}`}>
        <div className="container mx-auto px-4">
          <h2 className={`text-3xl font-bold mb-12 text-center ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
            Our <span className="text-vmil-blue">{title}</span>
          </h2>
          
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {products.map((product, index) => (
              <div 
                key={index}
                className={`group rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl ${
                  isDark 
                    ? 'bg-vmil-blue/50 hover:bg-vmil-blue/70' 
                    : 'bg-white hover:shadow-vmil-blue/10'
                }`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                    {product.title}
                  </h3>
                  {product.description && (
                    <p className={`text-sm ${isDark ? 'text-white/70' : 'text-vmil-steel'}`}>
                      {product.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative w-full py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-vmil-blue via-black to-vmil-blue" />
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 bg-vmil-blue/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
              Need Custom
              <span className="block text-vmil-blue">{title}?</span>
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              We specialize in manufacturing custom equipment tailored to your specific requirements. 
              Contact us to discuss your project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:contactus@vmil.in"
                className="inline-flex h-14 items-center justify-center rounded-xl bg-vmil-blue px-8 text-lg font-bold text-white shadow-lg shadow-vmil-blue/25 transition-all hover:shadow-xl hover:shadow-vmil-blue/30 hover:scale-[1.02]"
              >
                Get a Quote
              </a>
              <a 
                href="tel:+914312500547"
                className="inline-flex h-14 items-center justify-center rounded-xl border-2 border-white/30 px-8 text-lg font-bold text-white transition-all hover:bg-white/10 hover:border-white/50"
              >
                Call: +91 431 2500 547
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

